import { useState } from 'preact/hooks';
import { fmtDate, fmtEur, fmtEurDelta, fmtEurMicro, fmtEurPrecise, fmtPct } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { GameState } from '../../engine/state';
import { update } from '../../engine/store';
import { DAYS_PER_YEAR, dateAtDay } from '../../engine/time';
import {
  LIVRET_RATE,
  emergencyTarget,
  emptyPiggy,
  livretOpen,
  monthlyIncome,
  monthlyLoanPayments,
  monthlySpending,
  networth,
  outstandingDebt,
  openLdds,
  openLep,
  passiveIncomeAverage,
  steadyIncome,
  placementsAverageResult,
  placementsSinceOpening,
  placementsResult,
  transferAllToSavings,
  transferToLdds,
  transferToLep,
  transferToLivret,
  withdrawFromLdds,
  withdrawFromLep,
  withdrawFromLivret,
} from '../../game/economy';
import { OVERDRAFT_INCIDENT_FEE, overdraftLimit } from '../../game/actions';
import { CAR, canBuyCar, carMonthlyCosts, carPrice, carUnderWarranty, sellCar } from '../../game/car';
import { startEvent } from '../../game/events';
import { COLOC_STAY, canMoveOut, colocMalus, colocWarning, housingMonthly, parentsMalus, studioSwitchCost } from '../../game/housing';
import {
  LDDS,
  LEP,
  LIVRET_A,
  accruedSavingsInterest,
  lddsRoom,
  lepEligible,
  lepIncomeCeiling,
  lepRoom,
  lepTaxableIncome,
  livretRoom,
  regulatedSavings,
  savingsRoom,
} from '../../game/livret';
import { avValue } from '../../game/av';
import { peaMonthlyAmount, peaValue } from '../../game/pea';
import { currentObjectives, nextPalier, palierLabel } from '../../game/progress';
import {
  TERM_DEPOSIT,
  TERM_OFFERS,
  accruedTermInterest,
  closeTermDepositEarly,
  earlyExitGross,
  grossInterest,
  netInterest,
  openTermDeposit,
  termDepositsMonthlyIncome,
  yearsLabel,
} from '../../game/termDeposit';
import { isLowEnergy } from '../../game/wellbeing';
import { energyMax } from '../../game/work';
import { homeValue } from '../../game/property';
import { Card, Line, Meter } from '../components';
import { formatDuration, signedEur, signedPct } from '../labels';
import { TreatsPanel } from '../Treats';
import { WellbeingGauge } from '../Wellbeing';

/**
 * Le découvert autorisé, et ce que coûte le rouge quand on y est.
 * Les agios montent en direct, comme les intérêts du livret, jusqu'au prélèvement de fin de mois.
 */
function OverdraftInfo({ state }: { state: GameState }) {
  const limit = overdraftLimit(state);
  const fee = fmtEur(OVERDRAFT_INCIDENT_FEE);
  return (
    <>
      {state.cash >= 0 ? (
        <p class="hint">
          {limit > 0 ? t('bank.authorized', { limit: fmtEur(limit) }) : t('bank.noAuthorized', { fee })}
        </p>
      ) : (
        <p class="warning">{-state.cash <= limit ? t('bank.inAuthorized') : t('bank.inUnauthorized', { fee })}</p>
      )}
      {(state.cash < 0 || state.accruedAgios > 0) && (
        <p class="live-counter live-counter-bad">
          {t('bank.accruedAgios', { amount: fmtEurMicro(state.accruedAgios) })}
        </p>
      )}
    </>
  );
}

/** La voiture : la chercher quand on a le permis, ou voir ce qu'elle vaut encore et la revendre. */
function CarCard({ state }: { state: GameState }) {
  const [confirming, setConfirming] = useState(false);
  if (state.hasCar) {
    const value = fmtEur(state.carValue);
    const firstYearOfNew = state.carBoughtNew && state.day - state.carBoughtDay < DAYS_PER_YEAR;
    const dateOf = (day: number) => fmtDate(dateAtDay(state.startDate, day));
    return (
      <Card title={t('car.title')}>
        <Line label={t('car.value')} value={value} />
        <p class="hint">{t(firstYearOfNew ? 'car.depreciationNew' : 'car.depreciation')}</p>
        <Line label={t('car.costs')} value={t('common.perMonth', { amount: fmtEur(carMonthlyCosts(state)) })} />
        {state.carNew ? (
          <p class="muted">{t('car.newUntil', { date: dateOf(state.carBoughtDay + CAR.newYears * DAYS_PER_YEAR) })}</p>
        ) : (
          carUnderWarranty(state) && <p class="muted">{t('car.warrantyUntil', { date: dateOf(state.carWarrantyUntil) })}</p>
        )}
        <p class="muted">{t('car.benefits')}</p>
        <div class="button-row">
          {confirming ? (
            <>
              <button
                class="button-primary"
                onClick={() => {
                  update(sellCar);
                  setConfirming(false);
                }}
              >
                {t('car.sellConfirm', { amount: value })}
              </button>
              <button onClick={() => setConfirming(false)}>{t('car.keep')}</button>
            </>
          ) : (
            <button onClick={() => setConfirming(true)}>{t('car.sell', { amount: value })}</button>
          )}
        </div>
      </Card>
    );
  }
  if (!canBuyCar(state)) return null;
  return (
    <Card title={t('car.titleNone')}>
      <p>
        {t('car.pitch', {
          usedPrice: fmtEur(carPrice(state, 'used')),
          usedMonthly: fmtEur(carMonthlyCosts(state, 'used')),
          newPrice: fmtEur(carPrice(state, 'new')),
          newMonthly: fmtEur(carMonthlyCosts(state, 'new')),
        })}
      </p>
      <p class="muted">{t('car.benefits')}</p>
      <div class="button-row">
        <button
          disabled={state.pendingEvent !== null}
          onClick={() => update((s) => startEvent(s, 'recherche_voiture'))}
        >
          {t('car.search')}
        </button>
        <button disabled={state.pendingEvent !== null} onClick={() => update((s) => startEvent(s, 'voiture_neuve'))}>
          {t('car.searchNew')}
        </button>
      </div>
    </Card>
  );
}

/** Les comptes à terme : ceux en cours, et le formulaire pour en ouvrir un, avec le brut et le net annoncés. */
function TermDepositsCard({ state }: { state: GameState }) {
  const [amount, setAmount] = useState(TERM_DEPOSIT.minimum);
  const [years, setYears] = useState(2);
  const [closing, setClosing] = useState<number | null>(null);
  if (!livretOpen(state)) return null;
  const max = Math.floor(Math.max(0, state.cash) / 100) * 100;
  const offer = TERM_OFFERS.find((item) => item.years === years) ?? TERM_OFFERS[0];
  const chosen = Math.max(TERM_DEPOSIT.minimum, Math.min(amount, max));
  const gross = grossInterest(chosen, offer.rate, years * DAYS_PER_YEAR);
  const maturity = fmtDate(dateAtDay(state.startDate, state.day + years * DAYS_PER_YEAR));
  return (
    <Card title={t('term.title')}>
      <p class="muted">{t('term.intro')}</p>
      {state.termDeposits.map((deposit) => {
        const accrued = accruedTermInterest(state, deposit);
        const exitAmount = fmtEur(deposit.amount + netInterest(earlyExitGross(state, deposit)));
        return (
          <div key={deposit.id} class="term-deposit">
            <Line
              label={t('term.line', {
                amount: fmtEur(deposit.amount),
                duration: yearsLabel(deposit.years),
                rate: fmtPct(deposit.rate),
                date: fmtDate(dateAtDay(state.startDate, deposit.endDay)),
              })}
              value={fmtEur(deposit.amount + netInterest(accrued))}
            />
            <p class="hint">{t('term.accrued', { gross: fmtEurPrecise(accrued), net: fmtEurPrecise(netInterest(accrued)) })}</p>
            <div class="button-row">
              {closing === deposit.id ? (
                <>
                  <button
                    class="button-primary"
                    onClick={() => {
                      update((s) => closeTermDepositEarly(s, deposit.id));
                      setClosing(null);
                    }}
                  >
                    {t('term.closeConfirm', { amount: exitAmount })}
                  </button>
                  <button onClick={() => setClosing(null)}>{t('term.keep')}</button>
                </>
              ) : (
                <button onClick={() => setClosing(deposit.id)}>{t('term.close')}</button>
              )}
            </div>
          </div>
        );
      })}
      {max >= TERM_DEPOSIT.minimum ? (
        <>
          <div class="button-row">
            {TERM_OFFERS.map((item) => (
              <button
                key={item.years}
                class={`button-small ${item.years === years ? 'active' : ''}`}
                onClick={() => setYears(item.years)}
              >
                {t('term.offer', { duration: yearsLabel(item.years), rate: fmtPct(item.rate) })}
              </button>
            ))}
          </div>
          <div class="slider">
            <input
              type="range"
              min={TERM_DEPOSIT.minimum}
              max={max}
              step={100}
              value={chosen}
              aria-label={t('term.amount', { amount: fmtEur(chosen) })}
              onInput={(event) => setAmount(Number((event.target as HTMLInputElement).value))}
            />
            <span class="slider-value">{t('term.amount', { amount: fmtEur(chosen) })}</span>
          </div>
          <p class="hint">
            {t('term.preview', { date: maturity, gross: fmtEur(gross), net: fmtEur(netInterest(gross)) })}
          </p>
          <button class="button-primary" onClick={() => update((s) => openTermDeposit(s, chosen, years))}>
            {t('term.open', { amount: fmtEur(chosen) })}
          </button>
        </>
      ) : (
        <p class="hint">{t('term.needMoney', { amount: fmtEur(TERM_DEPOSIT.minimum) })}</p>
      )}
    </Card>
  );
}

/** Chez les parents pendant la vie active : ce que ça coûte au moral, et le bouton pour quitter le nid. */
function HomeCard({ state }: { state: GameState }) {
  if (canMoveOut(state)) {
    const malus = Math.round(parentsMalus(state));
    return (
      <Card title={t('home.title')}>
        <p class={malus > 0 ? 'warning' : 'muted'}>
          {malus > 0 ? t('home.malus', { points: malus }) : t('home.grace')}
        </p>
        <p class="muted">
          {t('home.pitch', {
            totalColoc: fmtEur(housingMonthly(state, 'coloc')),
            totalStudio: fmtEur(housingMonthly(state, 'studio')),
          })}
        </p>
        <button
          disabled={state.pendingEvent !== null}
          onClick={() => update((s) => startEvent(s, 'quitter_le_nid'))}
        >
          {t('home.search')}
        </button>
      </Card>
    );
  }
  if (colocWarning(state)) {
    const malus = Math.round(colocMalus(state));
    return (
      <Card title={t('coloc.title')}>
        <p class={malus > 0 ? 'warning' : 'muted'}>
          {malus >= COLOC_STAY.max
            ? t('coloc.malusMax', { points: COLOC_STAY.max })
            : malus > 0
              ? t('coloc.malus', { points: malus })
              : t('coloc.grace')}
        </p>
        <p class="muted">
          {t('coloc.pitch', {
            totalColoc: fmtEur(housingMonthly(state, 'coloc')),
            totalStudio: fmtEur(housingMonthly(state, 'studio')),
            cost: fmtEur(studioSwitchCost(state)),
          })}
        </p>
        <button
          disabled={state.pendingEvent !== null}
          onClick={() => update((s) => startEvent(s, 'demenager_studio'))}
        >
          {t('coloc.search')}
        </button>
      </Card>
    );
  }
  return null;
}

export function DashboardTab({ state }: { state: GameState }) {
  const adult = state.stage !== 'prologue';
  const hasLivret = livretOpen(state);
  const room = livretRoom(state);
  const lddsSpace = lddsRoom(state);
  const lepSpace = lepRoom(state);
  const lepIncome = { income: fmtEur(lepTaxableIncome(state)), ceiling: fmtEur(lepIncomeCeiling(state)) };
  const savings = regulatedSavings(state);
  const regular = steadyIncome(state);
  const termIncome = termDepositsMonthlyIncome(state);
  const placements = state.pea.open || state.av.open;
  const marketMonths = Math.max(state.pea.results.length, state.av.results.length);
  const sinceOpening = placementsSinceOpening(state);
  const palier = nextPalier(state);
  const income = monthlyIncome(state);
  const spending = monthlySpending(state);
  const loans = monthlyLoanPayments(state);
  const balance = income - spending - loans - state.budget.autoSaving - peaMonthlyAmount(state);
  const target = emergencyTarget(state);
  const objectives = currentObjectives(state);
  const max = energyMax(state);

  return (
    <div class="grid">
      {!adult && state.firstJobTargetDay !== null && (
        <Card>
          <p class="eyebrow">{t('dash.studentIncome')}</p>
          <p class="big-number">{fmtEur(income)}</p>
          <p class="muted">
            {t('dash.firstJobIn', { duration: formatDuration(state.firstJobTargetDay - state.day) })}
          </p>
          <p class="hint">{t('prologue.years.hint')}</p>
        </Card>
      )}

      {hasLivret && (
        <Card className="card-hero">
          <div class="hero-split">
            <div>
              <p class="eyebrow">{t('dash.passiveIncome')}</p>
              <p class="hero-number">{fmtEurPrecise(regular)}</p>
              <p class="muted">
                {t(state.lepOpen ? 'dash.passiveDetailAll' : state.lddsOpen ? 'dash.passiveDetailBoth' : 'dash.passiveDetail', {
                  yearly: fmtEur((regular - termIncome) * 12),
                  rate: fmtPct(LIVRET_RATE),
                  livret: fmtEur(regulatedSavings(state)),
                })}
              </p>
              {termIncome > 0 && <p class="muted">{t('dash.passiveTerm', { amount: fmtEurPrecise(termIncome) })}</p>}
              <p class="live-counter">
                {t('dash.accruedInterest', { amount: fmtEurMicro(accruedSavingsInterest(state)) })}
              </p>
            </div>
            {placements && (
              <div>
                <p class="eyebrow">{t('dash.marketAverage')}</p>
                <p class={`hero-number ${placementsAverageResult(state) < 0 ? 'tone-bad' : ''}`}>
                  {fmtEurDelta(placementsAverageResult(state))}
                </p>
                <p class={placementsResult(state) < 0 ? 'warning' : 'muted'}>
                  {marketMonths > 0
                    ? t('dash.marketDetail', { months: marketMonths, amount: fmtEurDelta(placementsResult(state)) })
                    : t('dash.marketNoMonth')}
                </p>
                {sinceOpening.deposits > 0 && (
                  <p class={sinceOpening.gain < 0 ? 'tone-bad' : 'tone-good'}>
                    {t('dash.marketSinceOpening', {
                      gain: signedEur(sinceOpening.gain),
                      percent: signedPct(sinceOpening.gain / sinceOpening.deposits),
                      deposits: fmtEur(sinceOpening.deposits),
                    })}
                  </p>
                )}
                <p class="hint">{t('dash.passiveMarketHint')}</p>
              </div>
            )}
          </div>
          {palier ? (
            <>
              <Meter value={passiveIncomeAverage(state)} max={palier.threshold(state)} label={t('dash.passiveIncome')} />
              <p class="muted">
                {t('dash.nextPalier', { label: t(palierLabel(state, palier)), amount: fmtEurPrecise(palier.threshold(state)) })}
              </p>
              {placements && (
                <p class="hint">{t('dash.palierAverage', { average: fmtEurDelta(passiveIncomeAverage(state)) })}</p>
              )}
            </>
          ) : (
            <p class="muted">{t('dash.allPaliers')}</p>
          )}
          {!placements && <p class="hint">{t('dash.passiveHint')}</p>}
        </Card>
      )}

      <Card>
        {state.energyUnlocked && (
          <>
            <p class="meter-label">
              {t('dash.energy')}
              <span>{t('dash.energyValue', { current: Math.floor(state.energy), max })}</span>
            </p>
            <Meter value={state.energy} max={max} tone="energy" label={t('dash.energy')} />
            {isLowEnergy(state) && <p class="warning">{t('work.lowEnergy')}</p>}
          </>
        )}
        <WellbeingGauge state={state} />
        {state.energyUnlocked && <TreatsPanel state={state} />}
      </Card>

      <Card title={t('dash.money')}>
        <Line label={t('dash.cash')} value={fmtEur(state.cash)} tone={state.cash < 0 ? 'bad' : undefined} />
        {state.overdraftAccepted !== null && <OverdraftInfo state={state} />}
        {(state.piggy > 0 || !hasLivret) && <Line label={t('dash.piggy')} value={fmtEur(state.piggy)} />}
        {hasLivret && (
          <Line label={t('dash.livret', { rate: fmtPct(LIVRET_A.rate) })} value={fmtEurPrecise(state.livret)} />
        )}
        {hasLivret && (
          <p class="hint">
            {room >= LIVRET_A.minOperation
              ? t('dash.livretRoom', { room: fmtEur(room) })
              : t('dash.livretFullHint')}{' '}
            {t('dash.quinzaines')}
          </p>
        )}
        {state.lddsOpen && <Line label={t('dash.ldds', { rate: fmtPct(LDDS.rate) })} value={fmtEurPrecise(state.ldds)} />}
        {state.lddsOpen && (
          <p class="hint">
            {lddsSpace >= LDDS.minOperation ? t('dash.lddsRoom', { room: fmtEur(lddsSpace) }) : t('dash.lddsFullHint')}
          </p>
        )}
        {state.lepOpen && <Line label={t('dash.lep', { rate: fmtPct(LEP.rate) })} value={fmtEurPrecise(state.lep)} />}
        {state.lepOpen &&
          (state.lepOverYears > 0 ? (
            <p class="warning">{t('dash.lepOver', lepIncome)}</p>
          ) : (
            <p class="hint">
              {lepSpace >= LEP.minOperation ? t('dash.lepRoom', { room: fmtEur(lepSpace) }) : t('dash.lepFullHint')}
            </p>
          ))}
        {adult &&
          hasLivret &&
          !state.lepOpen &&
          (lepEligible(state) ? (
            <div class="offer">
              <p class="hint">{t('dash.lepOffer', { rate: fmtPct(LEP.rate), ...lepIncome })}</p>
              <button disabled={state.cash < LEP.minOpening} onClick={() => update(openLep)}>
                {t('dash.openLep', { amount: fmtEur(LEP.minOpening) })}
              </button>
            </div>
          ) : (
            <p class="hint">{t('dash.lepNotEligible', { rate: fmtPct(LEP.rate), ...lepIncome })}</p>
          ))}
        {hasLivret && !state.lddsOpen && (
          <div class="offer">
            <p class="hint">{t('dash.lddsOffer', { rate: fmtPct(LDDS.rate) })}</p>
            <button disabled={state.cash < LDDS.minOperation} onClick={() => update(openLdds)}>
              {t('dash.openLdds', { amount: fmtEur(LDDS.minOperation) })}
            </button>
          </div>
        )}
        {state.pea.open && <Line label={t('dash.pea')} value={fmtEur(peaValue(state))} />}
        {state.av.open && <Line label={t('dash.av')} value={fmtEur(avValue(state))} />}
        {state.hasCar && <Line label={t('dash.car')} value={fmtEur(state.carValue)} />}
        {state.home && <Line label={t('dash.home')} value={fmtEur(homeValue(state))} />}
        {outstandingDebt(state) > 0 && (
          <Line label={t('dash.debt')} value={fmtEur(-outstandingDebt(state))} tone="bad" />
        )}
        {state.cardWithdrawn && !state.surendettement && <p class="warning">{t('dash.carteRetiree')}</p>}
        {state.ficp && !state.surendettement && <p class="warning">{t('dash.ficp')}</p>}
        {state.surendettement && (
          <p class="warning">{t('dash.plan', { months: state.surendettement.monthsLeft })}</p>
        )}
        <Line label={t('dash.networth')} value={fmtEur(networth(state))} tone="strong" />
        {hasLivret && spending > 0 && state.cash > 2 * spending && <p class="hint">{t('dash.idleCash')}</p>}
        {hasLivret && (
          <div class="button-row">
            {state.piggy > 0 && (
              <button class="button-primary" onClick={() => update(emptyPiggy)}>
                {t('dash.emptyPiggy')}
              </button>
            )}
            <button
              disabled={state.cash < 100 || room < LIVRET_A.minOperation}
              onClick={() => update((s) => transferToLivret(s, 100))}
            >
              {t('dash.toLivret', { amount: fmtEur(100) })}
            </button>
            <button
              disabled={state.cash < LIVRET_A.minOperation || savingsRoom(state) < LIVRET_A.minOperation}
              onClick={() => update(transferAllToSavings)}
            >
              {state.lddsOpen || state.lepOpen ? t('dash.allToSavings') : t('dash.allToLivret')}
            </button>
            <button disabled={state.livret < 100} onClick={() => update((s) => withdrawFromLivret(s, 100))}>
              {t('dash.fromLivret', { amount: fmtEur(100) })}
            </button>
            {state.lddsOpen && (
              <>
                <button
                  disabled={state.cash < 100 || lddsSpace < LDDS.minOperation}
                  onClick={() => update((s) => transferToLdds(s, 100))}
                >
                  {t('dash.toLdds', { amount: fmtEur(100) })}
                </button>
                <button disabled={state.ldds < 100} onClick={() => update((s) => withdrawFromLdds(s, 100))}>
                  {t('dash.fromLdds', { amount: fmtEur(100) })}
                </button>
              </>
            )}
            {state.lepOpen && (
              <>
                <button
                  disabled={state.cash < 100 || lepSpace < LEP.minOperation}
                  onClick={() => update((s) => transferToLep(s, 100))}
                >
                  {t('dash.toLep', { amount: fmtEur(100) })}
                </button>
                <button disabled={state.lep < 100} onClick={() => update((s) => withdrawFromLep(s, 100))}>
                  {t('dash.fromLep', { amount: fmtEur(100) })}
                </button>
              </>
            )}
          </div>
        )}
      </Card>

      <Card title={t('dash.month')}>
        <Line label={t('dash.income')} value={fmtEur(income)} tone="good" />
        <Line label={t('dash.spending')} value={fmtEur(-spending)} />
        {loans > 0 && <Line label={t('dash.loanPayments')} value={fmtEur(-loans)} />}
        {state.budget.autoSaving > 0 && <Line label={t('dash.autoSaving')} value={fmtEur(-state.budget.autoSaving)} />}
        <Line label={t('dash.balance')} value={fmtEur(balance)} tone={balance < 0 ? 'bad' : 'strong'} />
      </Card>

      <TermDepositsCard state={state} />
      <HomeCard state={state} />
      {adult && <CarCard state={state} />}

      {adult && (
        <Card title={t('dash.emergency')}>
          <Meter value={savings} max={target} label={t('dash.emergency')} />
          <p class="muted">{t('dash.emergencyProgress', { current: fmtEur(savings), target: fmtEur(target) })}</p>
        </Card>
      )}

      {objectives.length > 0 && (
        <Card title={adult ? t('dash.objectives') : t('dash.objectivesPrologue')}>
          <ul class="objectives">
            {objectives.map((objective) => {
              const done = state.objectivesDone.includes(objective.id);
              return (
                <li key={objective.id} class={done ? 'done' : ''}>
                  <span aria-hidden="true">{done ? '✓' : '○'}</span> {t(objective.label)}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
